import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    land_size: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        land_size: '',
        message: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-forest overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-sm tracking-[0.3em] uppercase mb-6 text-white/80"
          >
            Get in Touch
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight mb-8"
            data-testid="contact-headline"
          >
            Start the Conversation
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed"
          >
            Every landscape is unique. Tell us about your land, your goals, or the challenges 
            you are facing. We will explore how ecological design can help.
          </motion.p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-cream" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="lg:col-span-1"
            >
              <h2 className="font-serif text-2xl text-forest mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-medium text-forest mb-1">Location</h3>
                    <p className="text-foreground/70">
                      Sakleshpura, Karnataka<br />
                      India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-medium text-forest mb-1">Phone</h3>
                    <a 
                      href="tel:+919606591592" 
                      className="text-foreground/70 hover:text-forest transition-colors"
                      data-testid="contact-phone-link"
                    >
                      +91 9606591592
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-medium text-forest mb-1">Email</h3>
                    <a 
                      href="mailto:reachout@feralways.org" 
                      className="text-foreground/70 hover:text-forest transition-colors"
                      data-testid="contact-email-link"
                    >
                      reachout@feralways.org
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-sand rounded-2xl">
                <h3 className="font-serif text-lg text-forest mb-4">What to Expect</h3>
                <ul className="space-y-3 text-foreground/70 text-sm">
                  <li>• We'll respond within 2-3 business days</li>
                  <li>• Initial consultation to understand your land</li>
                  <li>• Custom proposal based on your needs</li>
                  <li>• No obligation to proceed</li>
                </ul>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              {success ? (
                <div className="bg-forest/5 border border-forest/20 rounded-2xl p-12 text-center" data-testid="success-message">
                  <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-forest" />
                  </div>
                  <h3 className="font-serif text-2xl text-forest mb-4">Thank You!</h3>
                  <p className="text-foreground/70 max-w-md mx-auto">
                    Your inquiry has been received. We'll review your message and get back to you 
                    within 2-3 business days.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-forest font-medium">
                        Name <span className="text-clay">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="bg-white border-border/50 rounded-xl h-12 input-focus"
                        data-testid="input-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-forest font-medium">
                        Email <span className="text-clay">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-white border-border/50 rounded-xl h-12 input-focus"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-forest font-medium">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="bg-white border-border/50 rounded-xl h-12 input-focus"
                        data-testid="input-phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-forest font-medium">
                        Land Location
                      </Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="District, State"
                        className="bg-white border-border/50 rounded-xl h-12 input-focus"
                        data-testid="input-location"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="land_size" className="text-forest font-medium">
                      Land Size
                    </Label>
                    <Input
                      id="land_size"
                      name="land_size"
                      value={formData.land_size}
                      onChange={handleChange}
                      placeholder="e.g., 5 acres, 2 hectares"
                      className="bg-white border-border/50 rounded-xl h-12 input-focus"
                      data-testid="input-land-size"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-forest font-medium">
                      Message <span className="text-clay">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell us about your land, goals, or challenges..."
                      className="bg-white border-border/50 rounded-xl input-focus resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-clay/10 border border-clay/20 rounded-xl text-clay text-sm" data-testid="error-message">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full md:w-auto"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
